/**
 * Single source of truth for the bespoke free-tool pages under `src/app/tools/`.
 *
 * Before this file, the same inventory was maintained as four independent
 * hand-kept lists (nav links, the content-pipeline guard, the sitemap's static
 * paths, the footer column). They drifted: the sitemap was missing 8 live tools
 * and shipped a 307, the nav was missing one tool and listed a redirect, and the
 * footer took a positional `.slice(0, 7)`. All four now derive from `FREE_TOOLS`,
 * and `registry.test.ts` asserts the registry matches the filesystem.
 *
 * DATA ONLY — no React, no `lucide-react`. This module is pulled into
 * `src/app/sitemap.ts` and, via `bespoke-tool-slugs.ts`, into the content-pipeline
 * worker. Icons are stored as NAMES and resolved by `<FreeToolIcon />`, which is
 * also what lets a definition cross a server/client boundary as props.
 */

import type { FreeToolJobStoredTool } from './types';

// `download` leads (2026-08-13): 30d free_tool_jobs volume has Facebook/Vimeo
// out-pulling every transcribe-convert and repurpose tool combined, even while
// Reddit was 100% broken and Vimeo was at 47% completion (both fixed same day
// — see FREE_TOOLS_STRATEGY.md Tier 3.5). Revisit if GSC clicks (which still
// favor transcribe-convert) diverge further from job volume once the fixes settle.
export const FREE_TOOL_CATEGORIES = ['download', 'transcribe-convert', 'repurpose'] as const;

export type FreeToolCategory = (typeof FREE_TOOL_CATEGORIES)[number];

export const FREE_TOOL_CATEGORY_META: Record<FreeToolCategory, { label: string; blurb: string }> = {
  'transcribe-convert': {
    label: 'Transcribe & Convert',
    blurb: 'Turn audio and video into text you can search, edit, and export.',
  },
  repurpose: {
    label: 'Repurpose Content',
    blurb: 'Turn a recording into something publishable.',
  },
  download: {
    label: 'Download Media',
    blurb: 'Pull the source file off any platform, then transcribe it.',
  },
};

/**
 * Icon names, not components. Resolved by `<FreeToolIcon />`.
 * `Sparkles` is deliberately absent and must stay that way (owner ban: reads as AI slop).
 */
export const FREE_TOOL_ICON_NAMES = [
  'AudioLines',
  'Calculator',
  'Captions',
  'FileCode2',
  'FileText',
  'FileVideo2',
  'Gauge',
  'ListOrdered',
  'MessagesSquare',
  'MonitorPlay',
  'Music4',
  'PenLine',
  'Presentation',
  'Quote',
  'Radio',
  'ScrollText',
  'Video',
  'Volume2',
  'WholeWord',
  'Youtube',
] as const;

export type FreeToolIconName = (typeof FREE_TOOL_ICON_NAMES)[number];

/** Platform marks with a local SVG under `public/images/tools/platforms/`. */
export const FREE_TOOL_BRANDS = [
  'YouTube',
  'Instagram',
  'TikTok',
  'X',
  'Facebook',
  'Reddit',
  'Vimeo',
  'SoundCloud',
  'Loom',
  'TED',
  'Apple Podcasts',
] as const;

export type FreeToolBrand = (typeof FREE_TOOL_BRANDS)[number];

export type FreeToolApplicationCategory =
  | 'MultimediaApplication'
  | 'BusinessApplication'
  | 'UtilitiesApplication';

export interface FreeToolDefinition {
  /** Route segment under /tools. Must match a folder in `src/app/tools/`. */
  slug: string;
  /** Nav, card, and footer label. Human, not the page <title>. */
  label: string;
  /** One line for nav + cards. Sentence case, no em dashes, under ~90 chars. */
  description: string;
  category: FreeToolCategory;
  iconName: FreeToolIconName;
  /** Platform the tool works on. Its tile shows that brand's mark instead of `iconName`. */
  brand?: FreeToolBrand;
  /**
   * `app`      real interactive client component
   * `guide`    informational only (see Workstream 5 — these are being converted)
   * `redirect` the folder exists but only calls redirect()
   */
  kind: 'app' | 'guide' | 'redirect';
  /** Surface opt-outs. Defaults are derived from `kind`, see the helpers below. */
  surfaces?: {
    nav?: boolean;
    /** Footer is a curated column, so this one is opt-IN rather than opt-out. */
    footer?: boolean;
    sitemap?: boolean;
    hub?: boolean;
  };
  /** Cross-links rendered as chips by the CTA band. Slugs, not hrefs. */
  relatedSlugs?: readonly string[];
  /**
   * Backend job kinds this page can produce. A typed LINK to `FREE_TOOL_KINDS`,
   * not a merge: those values are persisted to `free_tool_jobs.tool`, the mapping
   * is many-to-many, and several kinds (rumble/bluesky/embed/direct) have no page
   * at all. Pure client-side tools have none.
   */
  jobKinds?: readonly FreeToolJobStoredTool[];
  /** Feeds the SoftwareApplication schema. Defaults per category, see `applicationCategoryFor`. */
  applicationCategory?: FreeToolApplicationCategory;
}

export const FREE_TOOLS: readonly FreeToolDefinition[] = [
  // ---------------------------------------------------------------- transcribe
  {
    slug: 'free-audio-to-text',
    label: 'Free Audio-to-Text Converter',
    description: 'Turn an interview or episode into text you can edit, search, and export.',
    category: 'transcribe-convert',
    iconName: 'AudioLines',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['video-to-text', 'subtitle-generator', 'transcript-summary-generator'],
    jobKinds: ['free-audio-to-text'],
  },
  {
    slug: 'video-to-text',
    label: 'Video to Text Converter',
    description: 'Drop in an MP4 or a YouTube link and get a transcript you can publish.',
    category: 'transcribe-convert',
    iconName: 'FileVideo2',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['free-audio-to-text', 'subtitle-generator', 'youtube-transcript'],
    jobKinds: ['free-audio-to-text'],
  },
  {
    slug: 'subtitle-generator',
    label: 'Subtitle & SRT Generator',
    description: 'Caption a video for social or YouTube. Export SRT, VTT, or plain TXT.',
    category: 'transcribe-convert',
    iconName: 'Captions',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['srt-to-vtt-converter', 'video-to-text', 'free-audio-to-text'],
    jobKinds: ['free-audio-to-text'],
  },
  {
    slug: 'youtube-transcript',
    label: 'YouTube Transcript Generator',
    description: 'Paste a YouTube link and get the transcript. Free, no signup.',
    category: 'transcribe-convert',
    iconName: 'Captions',
    brand: 'YouTube',
    kind: 'app',
    relatedSlugs: [
      'tiktok-transcript',
      'instagram-transcript',
      'vimeo-transcript',
      'subtitle-generator',
      'video-to-text',
      'youtube-audio-downloader',
    ],
    jobKinds: ['youtube-transcript'],
  },
  {
    // Superseded by /tools/youtube-transcript (2026-08-27, YouTube-cluster
    // siege plan): same parent topic, two pages would cannibalize. The folder
    // only calls redirect(); registered so the pipeline guard still shadows
    // the slug, kept out of nav/hub/sitemap by the `kind: 'redirect'` defaults.
    slug: 'youtube-video-to-text',
    label: 'YouTube Video to Text',
    description: 'Redirects to the YouTube transcript generator.',
    category: 'transcribe-convert',
    iconName: 'FileText',
    brand: 'YouTube',
    kind: 'redirect',
  },
  {
    slug: 'tiktok-transcript',
    label: 'TikTok Transcript Generator',
    description: 'Paste a TikTok link and get a transcript. Free, no signup, no watermark.',
    category: 'transcribe-convert',
    iconName: 'Captions',
    brand: 'TikTok',
    kind: 'app',
    relatedSlugs: [
      'tiktok-downloader',
      'video-to-text',
      'subtitle-generator',
      'free-audio-to-text',
      'youtube-transcript',
    ],
    jobKinds: ['tiktok-transcript'],
  },
  {
    slug: 'instagram-transcript',
    label: 'Instagram Transcript Generator',
    description: 'Paste an Instagram Reel or video link and get a transcript. Free, no signup.',
    category: 'transcribe-convert',
    iconName: 'Captions',
    brand: 'Instagram',
    kind: 'app',
    relatedSlugs: [
      'tiktok-transcript',
      'instagram-downloader',
      'video-to-text',
      'subtitle-generator',
      'free-audio-to-text',
    ],
    jobKinds: ['instagram-transcript'],
  },
  {
    slug: 'vimeo-transcript',
    label: 'Vimeo Transcript Generator',
    description: 'Paste a Vimeo link and get the transcript. Free, no signup.',
    category: 'transcribe-convert',
    iconName: 'Captions',
    brand: 'Vimeo',
    kind: 'app',
    relatedSlugs: [
      'youtube-transcript',
      'vimeo-to-mp3',
      'subtitle-generator',
      'video-to-text',
      'free-audio-to-text',
    ],
    jobKinds: ['vimeo-transcript'],
  },
  {
    slug: 'how-to-transcribe-youtube-video-to-text',
    label: 'Transcribe YouTube Video',
    description: 'The step by step version: download the file, then export a transcript.',
    category: 'transcribe-convert',
    iconName: 'ScrollText',
    brand: 'YouTube',
    kind: 'app',
    relatedSlugs: ['youtube-transcript', 'youtube-video-downloader', 'youtube-audio-downloader'],
    jobKinds: ['youtube-mp3'],
  },
  {
    slug: 'text-to-speech',
    label: 'Free Text to Speech',
    description: 'Turn text into a downloadable MP3. Speaks whatever language you type in.',
    category: 'transcribe-convert',
    iconName: 'Volume2',
    kind: 'app',
    relatedSlugs: ['free-audio-to-text', 'subtitle-generator'],
    applicationCategory: 'MultimediaApplication',
  },
  {
    slug: 'srt-to-vtt-converter',
    label: 'SRT to VTT Converter',
    description: 'Convert caption files both ways in your browser. Nothing gets uploaded.',
    category: 'transcribe-convert',
    iconName: 'FileCode2',
    kind: 'app',
    relatedSlugs: ['subtitle-generator', 'transcript-word-counter'],
    applicationCategory: 'UtilitiesApplication',
  },
  {
    slug: 'transcript-word-counter',
    label: 'Transcript Word Counter',
    description: 'Check length and read time before you publish. Runs in your browser.',
    category: 'transcribe-convert',
    iconName: 'WholeWord',
    kind: 'app',
    relatedSlugs: ['transcript-quality-analyzer', 'transcript-summary-generator'],
    applicationCategory: 'UtilitiesApplication',
  },
  {
    slug: 'transcript-quality-analyzer',
    label: 'Transcript Quality Analyzer',
    description: 'Score a transcript 0 to 100 before you build anything on top of it.',
    category: 'transcribe-convert',
    iconName: 'Gauge',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['transcript-word-counter', 'free-audio-to-text'],
    applicationCategory: 'UtilitiesApplication',
  },
  {
    slug: 'transcription-cost-calculator',
    label: 'Transcription Cost Calculator',
    description: 'Compare your real monthly cost across Wisprs, Otter.ai, Rev, and Descript.',
    category: 'transcribe-convert',
    iconName: 'Calculator',
    kind: 'app',
    relatedSlugs: ['free-audio-to-text', 'transcript-quality-analyzer', 'video-to-text'],
    applicationCategory: 'UtilitiesApplication',
  },

  // ----------------------------------------------------------------- repurpose
  {
    slug: 'podcast-to-show-notes',
    label: 'Podcast Show Notes Generator',
    description: "Turn this week's episode into show notes you can paste into the description.",
    category: 'repurpose',
    iconName: 'ScrollText',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['chapter-generator', 'audio-to-quotes', 'podcast-to-twitter-thread'],
    jobKinds: ['free-audio-to-text'],
  },
  {
    slug: 'chapter-generator',
    label: 'YouTube Chapters Generator',
    description: 'Get description-ready chapter markers you can paste straight into YouTube.',
    category: 'repurpose',
    iconName: 'ListOrdered',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['podcast-to-show-notes', 'transcript-summary-generator', 'blog-post-generator'],
    jobKinds: ['free-audio-to-text'],
  },
  {
    slug: 'blog-post-generator',
    label: 'Audio/Video to Blog Post',
    description: 'Turn a recording into a publish-ready draft instead of a wall of transcript.',
    category: 'repurpose',
    iconName: 'PenLine',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['transcript-summary-generator', 'podcast-to-show-notes', 'audio-to-quotes'],
    jobKinds: ['free-audio-to-text'],
  },
  {
    slug: 'transcript-summary-generator',
    label: 'Transcript Summary Generator',
    description: 'Get the TL;DR and key points without rereading the whole transcript.',
    category: 'repurpose',
    iconName: 'FileText',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['blog-post-generator', 'chapter-generator', 'podcast-to-show-notes'],
    jobKinds: ['free-audio-to-text'],
  },
  {
    slug: 'podcast-to-twitter-thread',
    label: 'Podcast to X Thread',
    description: 'Turn an episode into a thread you can post the same day.',
    category: 'repurpose',
    iconName: 'MessagesSquare',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['audio-to-quotes', 'blog-post-generator', 'podcast-to-show-notes'],
    jobKinds: ['free-audio-to-text'],
  },
  {
    slug: 'audio-to-quotes',
    label: 'Pull-Quote Extractor',
    description: 'Pull the most quotable lines out of an episode, word for word.',
    category: 'repurpose',
    iconName: 'Quote',
    kind: 'app',
    surfaces: { footer: true },
    relatedSlugs: ['podcast-to-twitter-thread', 'podcast-to-show-notes', 'blog-post-generator'],
    jobKinds: ['free-audio-to-text'],
  },

  // ------------------------------------------------------------------ download
  // Order is usage-ranked (30d free_tool_jobs volume + 90d GSC), not
  // alphabetical/platform-grouped: facebook and vimeo are, by a wide margin,
  // the two highest-volume free tools in the product (see FREE_TOOLS_STRATEGY.md
  // Tier 3.5 revision, 2026-08-13). Reddit stays mid-pack pending verification
  // of its proxy fix rather than leading with a tool that was 0/10 completions.
  {
    slug: 'facebook-video-downloader',
    label: 'Facebook Video Downloader',
    description: 'Save Facebook videos, Reels, and Watch posts as MP4 or MP3.',
    category: 'download',
    iconName: 'Video',
    brand: 'Facebook',
    kind: 'app',
    relatedSlugs: ['instagram-downloader', 'vimeo-to-mp3', 'twitter-video-to-mp3'],
    jobKinds: ['facebook-mp4', 'facebook-mp3'],
  },
  {
    // No usage data yet (shipped 2026-08-13) — placed third on demand
    // expectation, not measured volume: it's the single highest-volume
    // missing platform per FREE_TOOLS_STRATEGY.md's Tier 3.5 revision.
    // Revisit position once 30d free_tool_jobs volume exists.
    slug: 'instagram-downloader',
    label: 'Instagram Reels Downloader',
    description: 'Save Instagram Reels and videos as MP4, or pull the audio as MP3.',
    category: 'download',
    iconName: 'Video',
    brand: 'Instagram',
    kind: 'app',
    relatedSlugs: ['facebook-video-downloader', 'tiktok-downloader', 'vimeo-to-mp3'],
    jobKinds: ['instagram-mp4', 'instagram-mp3'],
  },
  {
    slug: 'vimeo-to-mp3',
    label: 'Vimeo to MP3',
    description: 'Save a public Vimeo video as MP4, or pull the audio to transcribe.',
    category: 'download',
    iconName: 'MonitorPlay',
    brand: 'Vimeo',
    kind: 'app',
    relatedSlugs: ['vimeo-transcript', 'facebook-video-downloader', 'loom-to-mp3', 'ted-talk-transcript'],
    jobKinds: ['vimeo-mp3', 'vimeo-mp4'],
  },
  {
    slug: 'youtube-video-downloader',
    label: 'YouTube Video Downloader',
    description: 'Save a YouTube video as MP4 or MP3, then send it to transcription.',
    category: 'download',
    iconName: 'Youtube',
    brand: 'YouTube',
    kind: 'app',
    relatedSlugs: ['youtube-audio-downloader', 'youtube-transcript'],
    jobKinds: ['youtube-mp4', 'youtube-mp3'],
  },
  {
    slug: 'youtube-audio-downloader',
    label: 'YouTube Audio Downloader',
    description: 'Pull the audio from a YouTube video, then turn it into text.',
    category: 'download',
    iconName: 'Music4',
    brand: 'YouTube',
    kind: 'app',
    relatedSlugs: ['youtube-video-downloader', 'youtube-transcript'],
    jobKinds: ['youtube-mp3'],
  },
  {
    slug: 'twitter-video-to-mp3',
    label: 'Twitter / X Video to MP3',
    description: 'Save an X video as MP4 or pull just the audio as MP3.',
    category: 'download',
    iconName: 'MessagesSquare',
    brand: 'X',
    kind: 'app',
    relatedSlugs: ['facebook-video-downloader', 'tiktok-downloader', 'reddit-video-downloader'],
    jobKinds: ['twitter-mp4', 'twitter-mp3'],
  },
  {
    slug: 'reddit-video-downloader',
    label: 'Reddit Video Downloader',
    description: 'Save Reddit and v.redd.it videos as MP4, or just the audio.',
    category: 'download',
    iconName: 'Video',
    brand: 'Reddit',
    kind: 'app',
    relatedSlugs: ['facebook-video-downloader', 'tiktok-downloader', 'twitter-video-to-mp3'],
    jobKinds: ['reddit-mp4', 'reddit-mp3'],
  },
  {
    slug: 'tiktok-downloader',
    label: 'Free TikTok Downloader',
    description: 'Save a TikTok as video or audio, with no watermark on the free tier.',
    category: 'download',
    iconName: 'Video',
    brand: 'TikTok',
    kind: 'app',
    relatedSlugs: ['twitter-video-to-mp3', 'facebook-video-downloader', 'reddit-video-downloader'],
    jobKinds: ['tiktok-mp4', 'tiktok-mp3'],
  },
  {
    slug: 'podcast-to-text',
    label: 'Podcast to Text',
    description: 'Download an episode from Apple, SoundCloud, or Libsyn, then transcribe it.',
    category: 'download',
    iconName: 'Radio',
    brand: 'Apple Podcasts',
    kind: 'app',
    relatedSlugs: ['podcast-to-show-notes', 'free-audio-to-text', 'transcript-summary-generator'],
    jobKinds: ['podcast-mp3'],
  },
  {
    slug: 'ted-talk-transcript',
    label: 'TED Talk Transcript',
    description: 'Get the audio from any TED Talk, then turn it into a transcript.',
    category: 'download',
    iconName: 'Presentation',
    brand: 'TED',
    kind: 'app',
    relatedSlugs: ['vimeo-to-mp3', 'youtube-audio-downloader', 'free-audio-to-text'],
    jobKinds: ['ted-mp3'],
  },
  {
    slug: 'loom-to-mp3',
    label: 'Loom to MP3',
    description: 'Pull the audio from a Loom recording so you can transcribe or archive it.',
    category: 'download',
    iconName: 'MonitorPlay',
    brand: 'Loom',
    kind: 'app',
    relatedSlugs: ['vimeo-to-mp3', 'ted-talk-transcript', 'free-audio-to-text'],
    jobKinds: ['loom-mp3'],
  },
  {
    // Dedicated page + job kind, split out from the generic podcast-to-text
    // host list 2026-08-13 for its own keyword targeting and attribution.
    slug: 'soundcloud-downloader',
    label: 'SoundCloud Downloader',
    description: 'Save a public SoundCloud track as MP3, then send it to transcription.',
    category: 'download',
    iconName: 'Music4',
    brand: 'SoundCloud',
    kind: 'app',
    relatedSlugs: ['podcast-to-text', 'loom-to-mp3', 'free-audio-to-text'],
    jobKinds: ['soundcloud-mp3'],
  },
  {
    // Folder exists but only calls redirect() to /tools/youtube-video-downloader.
    // Registered so the pipeline guard still shadows the slug, but kept out of
    // nav/hub/sitemap by the `kind: 'redirect'` defaults — a 307 in the sitemap
    // is a GSC "Page with redirect" not-indexed row.
    slug: 'youtube-mp3',
    label: 'YouTube to MP3',
    description: 'Redirects to the YouTube video downloader.',
    category: 'download',
    iconName: 'Music4',
    brand: 'YouTube',
    kind: 'redirect',
  },
];

export const FREE_TOOL_BY_SLUG: ReadonlyMap<string, FreeToolDefinition> = new Map(
  FREE_TOOLS.map((tool) => [tool.slug, tool])
);

export function freeToolHref(slug: string): string {
  return `/tools/${slug}`;
}

export function getFreeTool(slug: string): FreeToolDefinition | undefined {
  return FREE_TOOL_BY_SLUG.get(slug);
}

/** A redirect is not a destination: excluded from nav, hub, and sitemap by default. */
function isLinkable(tool: FreeToolDefinition): boolean {
  return tool.kind !== 'redirect';
}

export function getNavFreeTools(): FreeToolDefinition[] {
  return FREE_TOOLS.filter((t) => t.surfaces?.nav ?? isLinkable(t));
}

export function getHubFreeTools(): FreeToolDefinition[] {
  return FREE_TOOLS.filter((t) => t.surfaces?.hub ?? isLinkable(t));
}

/** Opt-in, unlike the other surfaces: the footer column is a curated subset. */
export function getFooterFreeTools(): FreeToolDefinition[] {
  return FREE_TOOLS.filter((t) => t.surfaces?.footer === true);
}

export function getSitemapFreeToolPaths(): string[] {
  return FREE_TOOLS.filter((t) => t.surfaces?.sitemap ?? isLinkable(t)).map((t) =>
    freeToolHref(t.slug)
  );
}

export interface FreeToolCategoryGroup {
  category: FreeToolCategory;
  label: string;
  blurb: string;
  tools: FreeToolDefinition[];
}

/** Nav + hub grouping. Categories keep `FREE_TOOL_CATEGORIES` order; empty groups are dropped. */
export function getFreeToolsByCategory(
  tools: readonly FreeToolDefinition[] = getNavFreeTools()
): FreeToolCategoryGroup[] {
  return FREE_TOOL_CATEGORIES.map((category) => ({
    category,
    ...FREE_TOOL_CATEGORY_META[category],
    tools: tools.filter((t) => t.category === category),
  })).filter((group) => group.tools.length > 0);
}

/** Resolves `relatedSlugs` to definitions, silently dropping unknown or self references. */
export function resolveRelatedTools(slug: string): FreeToolDefinition[] {
  const tool = FREE_TOOL_BY_SLUG.get(slug);

  if (!tool?.relatedSlugs) return [];

  return tool.relatedSlugs
    .filter((related) => related !== slug)
    .map((related) => FREE_TOOL_BY_SLUG.get(related))
    .filter((related): related is FreeToolDefinition => related !== undefined);
}

const DEFAULT_APPLICATION_CATEGORY: Record<FreeToolCategory, FreeToolApplicationCategory> = {
  download: 'MultimediaApplication',
  'transcribe-convert': 'BusinessApplication',
  repurpose: 'BusinessApplication',
};

/**
 * Normalizes what used to be a 3-way split across hand-rolled page schemas
 * (plus a 4th hardcoded opinion in `content/seo.ts`).
 */
export function applicationCategoryFor(tool: FreeToolDefinition): FreeToolApplicationCategory {
  return tool.applicationCategory ?? DEFAULT_APPLICATION_CATEGORY[tool.category];
}
